/**
 * Universal Tracking Manager - Optimized Version
 * Centralized tracking for ALL theme interactions
 * Best Practice: Single dataLayer path, let GTM handle platform routing
 */

class UniversalTracking {
  constructor() {
    // Initialize dataLayer if it doesn't exist
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    
    this.debugMode = this.getDebugMode();
    
    if (this.debugMode) {
      console.log('🎯 Universal Tracking initialized');
    }

    // Auto-initialize tracking listeners
    this.initializeAutoTracking();
  }

  getDebugMode() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('gtm_debug') === 'true' || 
           localStorage.getItem('gtm_debug') === 'true' ||
           window.location.hostname.includes('myshopify.com') ||
           (window.themeSettings && window.themeSettings.gtm_debug_mode);
  }

  /**
   * Core method: Push structured data to dataLayer only
   * Let GTM handle all tag firing and platform routing
   */
  track(eventData) {
    // Check if GTM is enabled via theme settings
    if (window.themeSettings && !window.themeSettings.gtm_enabled) {
      if (this.debugMode) {
        console.log('🚫 GTM disabled in theme settings, skipping tracking:', eventData);
      }
      return;
    }

    if (this.debugMode) {
      console.log('📊 Tracking Event:', eventData);
    }

    // Add standard context to all events
    const enrichedData = {
      ...eventData,
      ...this.getPageContext(),
      _timestamp: Date.now()
    };

    // Single source of truth: dataLayer
    window.dataLayer.push(enrichedData);

    // Optional: Custom event for non-GTM integrations
    document.dispatchEvent(new CustomEvent('tracking:universal', {
      detail: enrichedData
    }));
  }

  /**
   * Auto-initialize common tracking (centralized approach)
   */
  initializeAutoTracking() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAutoTracking());
    } else {
      this.setupAutoTracking();
    }
  }

  setupAutoTracking() {
    this.trackPageView();
    this.trackFormSubmissions();
    this.trackLinkClicks();
    this.trackScrollDepth();
    
    // Page-specific tracking
    if (this.getPageType() === 'product') {
      this.trackProductPageInteractions();
    }
  }

  /**
   * ===== STICKY ADD-TO-CART TRACKING =====
   */
  trackStickyATC(eventName, data = {}) {
    this.track({
      event: 'sticky_atc_interaction',
      event_category: 'sticky_add_to_cart',
      event_action: eventName,
      event_label: data.variant || 'default',
      // GTM-friendly parameter names
      sticky_atc_variant: data.variant,
      sticky_atc_device: data.device_type,
      sticky_atc_product_id: data.product_id,
      sticky_atc_product_title: data.product_title,
      sticky_atc_product_type: data.product_type,
      ...data
    });
  }

  trackStickyATCPurchase(productData) {
    this.track({
      event: 'add_to_cart',
      event_category: 'ecommerce',
      event_action: 'add_to_cart_sticky',
      event_label: productData.product_title,
      // GA4 Enhanced Ecommerce structure
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: productData.price ? (productData.price / 100) : 0,
        items: [{
          item_id: productData.variant_id?.toString() || '',
          item_name: productData.product_title || '',
          item_category: productData.product_type || '',
          item_variant: productData.variant_title || '',
          price: productData.price ? (productData.price / 100) : 0,
          quantity: productData.quantity || 1
        }]
      },
      // Additional context
      add_to_cart_method: 'sticky_button',
      add_to_cart_device: productData.device_type,
      add_to_cart_variant: productData.variant,
      ...productData
    });
  }

  /**
   * ===== REVIEW/RATING TRACKING =====
   */
  trackReviewScroll(data = {}) {
    this.track({
      event: 'review_interaction',
      event_category: 'product_reviews',
      event_action: 'rating_click_scroll',
      event_label: 'scroll_to_reviews',
      // Custom parameters
      review_trigger_type: data.trigger_type,
      review_stars_visible: data.rating_stars_visible,
      review_widget_present: data.reviews_widget_present,
      review_product_id: data.product_id,
      review_product_title: data.product_title,
      review_product_type: data.product_type,
      ...data
    });
  }

  /**
   * ===== GEO-REDIRECT TRACKING =====
   */
  trackGeoRedirect(eventName, data = {}) {
    this.track({
      event: 'geo_redirect_interaction',
      event_category: 'geo_redirect',
      event_action: eventName,
      event_label: data.detected_country || 'unknown',
      // GTM-friendly parameters
      geo_detected_country: data.detected_country,
      geo_suggested_store: data.suggested_store,
      geo_current_region: data.current_region,
      geo_interaction_type: data.interaction_type,
      geo_notification_type: data.notification_type,
      geo_is_restricted: data.is_shipping_restricted,
      geo_store_url: data.store_url,
      ...data
    });
  }

  /**
   * ===== UNIVERSAL AUTO-TRACKING METHODS =====
   */
  trackPageView() {
    this.track({
      event: 'page_view',
      event_category: 'page_interaction',
      event_action: 'page_view',
      event_label: this.getPageType()
    });
  }

  trackProductPageInteractions() {
    // Auto-track review scroll clicks
    const reviewElements = document.querySelectorAll('[data-scroll-to-reviews]');
    reviewElements.forEach(element => {
      ['click', 'keydown'].forEach(eventType => {
        element.addEventListener(eventType, (e) => {
          if (eventType === 'keydown' && !['Enter', ' '].includes(e.key)) return;
          
          const productData = this.getProductData();
          this.trackReviewScroll({
            product_id: productData?.id,
            product_title: productData?.title,
            product_type: productData?.type,
            trigger_type: eventType === 'keydown' ? 'keyboard' : 'click',
            rating_stars_visible: reviewElements.length > 0,
            reviews_widget_present: !!document.querySelector('[data-oke-widget]')
          });
        });
      });
    });

    // Auto-track variant changes
    const variantSelectors = document.querySelectorAll('[name="id"], .product-form__input input[type="radio"]');
    variantSelectors.forEach(element => {
      element.addEventListener('change', () => {
        this.track({
          event: 'product_interaction',
          event_category: 'product',
          event_action: 'variant_change',
          event_label: element.value,
          product_id: this.getProductData()?.id,
          variant_id: element.value
        });
      });
    });

    // Auto-track image gallery interactions
    const galleryImages = document.querySelectorAll('.product__media img, [data-media-id]');
    galleryImages.forEach((img, index) => {
      img.addEventListener('click', () => {
        this.track({
          event: 'product_interaction',
          event_category: 'product',
          event_action: 'image_click',
          event_label: `image_${index + 1}`,
          product_id: this.getProductData()?.id,
          image_position: index + 1
        });
      });
    });
  }

  trackFormSubmissions() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      const formType = this.getFormType(form);
      
      this.track({
        event: 'form_submission',
        event_category: 'form',
        event_action: 'submit',
        event_label: formType,
        form_type: formType,
        form_id: form.id || 'unknown',
        form_action: form.action || 'unknown'
      });
    });
  }

  trackLinkClicks() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const linkType = this.getLinkType(link);
      
      this.track({
        event: 'link_click',
        event_category: 'navigation',
        event_action: 'click',
        event_label: linkType,
        link_url: link.href,
        link_text: link.textContent?.trim().substring(0, 100),
        link_type: linkType,
        link_external: !link.href.includes(window.location.hostname)
      });
    });
  }

  trackScrollDepth() {
    let maxScroll = 0;
    const milestones = [25, 50, 75, 90, 100];
    
    const trackScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        
        const milestone = milestones.find(m => scrollPercent >= m && maxScroll < m);
        if (milestone) {
          this.track({
            event: 'scroll_depth',
            event_category: 'engagement',
            event_action: 'scroll',
            event_label: `${milestone}%`,
            scroll_depth: milestone
          });
        }
      }
    };

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          trackScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /**
   * ===== UTILITY METHODS =====
   */
  getProductData() {
    try {
      // Try multiple sources for product data
      const sources = [
        () => {
          const element = document.querySelector('[data-product-json]');
          return element ? JSON.parse(element.textContent) : null;
        },
        () => window.ShopifyAnalytics?.meta?.product,
        () => window.product,
        () => {
          // Extract from JSON-LD structured data
          const jsonLd = document.querySelector('script[type="application/ld+json"]');
          if (jsonLd) {
            const data = JSON.parse(jsonLd.textContent);
            return data['@type'] === 'Product' ? data : null;
          }
          return null;
        }
      ];

      for (const source of sources) {
        try {
          const data = source();
          if (data) return data;
        } catch (e) {
          continue;
        }
      }

      return null;
    } catch (error) {
      if (this.debugMode) {
        console.warn('Failed to get product data:', error);
      }
      return null;
    }
  }

  getPageContext() {
    return {
      page_type: this.getPageType(),
      page_url: window.location.href,
      page_title: document.title,
      page_path: window.location.pathname,
      page_search: window.location.search,
      customer_id: window.ShopifyAnalytics?.meta?.page?.customerId || null,
      customer_logged_in: !!window.ShopifyAnalytics?.meta?.page?.customerId,
      shop_currency: window.Shopify?.currency?.active || 'USD',
      shop_id: window.Shopify?.shop || null
    };
  }

  getPageType() {
    const body = document.body;
    if (body.classList.contains('template-product')) return 'product';
    if (body.classList.contains('template-collection')) return 'collection';
    if (body.classList.contains('template-cart')) return 'cart';
    if (body.classList.contains('template-index')) return 'home';
    if (body.classList.contains('template-search')) return 'search';
    if (body.classList.contains('template-blog')) return 'blog';
    if (body.classList.contains('template-article')) return 'article';
    if (body.classList.contains('template-page')) return 'page';
    return 'other';
  }

  getFormType(form) {
    if (form.action?.includes('/cart/add')) return 'add_to_cart';
    if (form.action?.includes('/contact')) return 'contact';
    if (form.action?.includes('/newsletter')) return 'newsletter';
    if (form.classList.contains('newsletter')) return 'newsletter';
    if (form.querySelector('[name="contact[email]"]')) return 'newsletter';
    if (form.querySelector('[name="email"]')) return 'email_capture';
    return form.id || form.className || 'unknown';
  }

  getLinkType(link) {
    const href = link.href;
    if (href.includes('/products/')) return 'product';
    if (href.includes('/collections/')) return 'collection';
    if (href.includes('/cart')) return 'cart';
    if (href.includes('/account')) return 'account';
    if (href.includes('mailto:')) return 'email';
    if (href.includes('tel:')) return 'phone';
    if (!href.includes(window.location.hostname)) return 'external';
    return 'internal';
  }

  /**
   * Manual tracking methods for specific use cases
   */
  trackCustomEvent(eventName, category, action, data = {}) {
    this.track({
      event: eventName,
      event_category: category,
      event_action: action,
      event_label: data.label || action,
      ...data
    });
  }

  trackEcommerce(action, items, value = 0, currency = 'USD') {
    this.track({
      event: action,
      event_category: 'ecommerce',
      event_action: action,
      currency: currency,
      value: value,
      ecommerce: {
        currency: currency,
        value: value,
        items: items
      }
         });
   }
}

// Initialize Universal Tracking
window.UniversalTracking = window.UniversalTracking || new UniversalTracking();

// Backwards compatibility
window.GTMTracking = window.UniversalTracking;

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = UniversalTracking;
} 